import { useMemo } from 'react'

import {
    UseQueryOptions,
    useQuery,
    useQueryClient,
    UseQueryResult,
    useMutation,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useSelector } from 'react-redux'

import {
    CerebroPaginatedResponse,
    CerebroResourceResponse,
} from 'api/Cerebro/types'
import { DATES } from 'const/filters'
import { useOrgContext } from 'context/OrgContext'
import { useUserOverrideContext } from 'context/UserOverrideContext'
import { notifyAsyncExportStarted } from 'features/Reports/api/notifyAsyncExportStarted'
import {
    ColumnsFormatted,
    formatColumns as defaultFormatColumns,
    formatCurrency,
    formatMetrics,
    formatPagination,
    formatPeriodDeltaDateRange,
    formatSorter,
} from 'helpers/params'
import { useCerebroApiRequest } from 'hooks'
import { CobaltPath } from 'routing/Path'
import { selectTableSettings } from 'selectors/ui'
import {
    CurrencyCode,
    Field,
    FiltersState,
    PageFilters,
    RootReduxState,
    Report,
} from 'types'

type FormatBannerFiltersFn = (
    filters: PageFilters | FiltersState,
    hasHourlyData?: boolean,
    fiscalYearStartMonth?: number
) => any

// This type is a common helper for hooks that
// wrap the CerebroPaginatedMetricDataQueryProps and CerebroPaginatedDataQueryParamsProps
// to build a simplified query hook
export type CerebroPaginatedMetricQueryWrapperProps<T> = Omit<
    CerebroPaginatedMetricDataQueryProps<T>,
    'queryKeyPrefix' | 'apiCall' | 'params'
> &
    Pick<
        CerebroPaginatedDataQueryParamsProps<T>,
        'filters' | 'currencyCode' | 'tablePath'
    >

export type CerebroDownloadQueryWrapperProps<T> = Omit<
    CerebroDownloadQueryProps<T>,
    'reportName' | 'queryKeyPrefix' | 'apiCall' | 'params'
> &
    Pick<
        CerebroDownloadParamsProps<T>,
        'filters' | 'currencyCode' | 'tablePath'
    >

export interface CerebroPaginatedDataQueryParamsProps<T> {
    tablePath: CobaltPath
    filters: PageFilters
    currencyCode?: CurrencyCode
    groupBy?: string
    additionalParams?: Record<string, unknown>
    formatFilters: FormatBannerFiltersFn
    metrics: Field<T>[]
    ignoreMetrics?: string[]
    periodDateFilterFallback?: string
    periodDateFilterPrefix?: string
    rangeLag?: number
}

export function useCerebroPaginatedMetricDataQueryParams<T>({
    tablePath,
    filters,
    currencyCode,
    groupBy,
    additionalParams,
    formatFilters,
    metrics,
    ignoreMetrics,
    periodDateFilterFallback,
    periodDateFilterPrefix,
    rangeLag,
}: CerebroPaginatedDataQueryParamsProps<T>): Record<string, unknown> {
    const { org } = useOrgContext()
    const fiscalYearStartMonth = org?.fiscal_year_start_month
    const { pagination, sorter } = useSelector((state: RootReduxState) =>
        selectTableSettings(state, tablePath)
    )
    const {
        showPeriodDeltas,
        periodDeltaType,
        periodDeltaDateRange,
        showTotalRow,
    } = useSelector((state: RootReduxState) =>
        selectTableSettings(state, tablePath)
    )

    const additionalMetricParams = {
        ...formatMetrics(
            metrics,
            showPeriodDeltas,
            showTotalRow,
            ignoreMetrics
        ),
        ...formatPeriodDeltaDateRange(
            showPeriodDeltas,
            periodDeltaType,
            periodDeltaDateRange,
            filters[DATES] ?? periodDateFilterFallback,
            periodDateFilterPrefix,
            rangeLag,
            fiscalYearStartMonth
        ),
        ...additionalParams,
    }

    const params: Record<string, unknown> = {
        ...formatPagination(pagination),
        ...formatSorter(sorter),
        ...formatFilters(filters, false, fiscalYearStartMonth),
        ...formatCurrency(currencyCode),
        ...additionalMetricParams,
    }
    if (groupBy) {
        params.group_by = groupBy
    }

    return params
}

interface CerebroPaginatedDataQueryProps<T> {
    queryKeyPrefix: string[]
    options?: Omit<
        UseQueryOptions<CerebroPaginatedResponse<T> | null, AxiosError>,
        'queryKey' | 'queryFn'
    >
    params: Record<string, unknown>
    apiCall: (
        params: Record<string, unknown>,
        options?: Record<string, unknown>
    ) => Promise<CerebroPaginatedResponse<T>>
    formatApiResponse?: (
        data: CerebroPaginatedResponse<T>
    ) => CerebroPaginatedResponse<T>
}

interface CerebroPaginatedDataQueryResult<T> {
    result: UseQueryResult<CerebroPaginatedResponse<T> | null, AxiosError>
    updateRecord: (data: { rowIndex: number; record: T }) => void
    invalidateAllRelatedQueries: () => void
}

function useCerebroPaginatedDataQuery<T>({
    queryKeyPrefix,
    options,
    params,
    apiCall,
    formatApiResponse,
}: CerebroPaginatedDataQueryProps<T>): CerebroPaginatedDataQueryResult<T> {
    const queryClient = useQueryClient()

    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })
    const { userOverride } = useUserOverrideContext()
    const { orgId } = useOrgContext()

    const apiOptions = {
        headers: {
            userId: userOverride?.userId,
            orgId: userOverride?.organizationId,
            orgGroupId: userOverride?.organizationGroupId,
        },
    }

    const queryKey = [
        ...queryKeyPrefix,
        orgId,
        {
            ...params,
        },
    ]

    return {
        invalidateAllRelatedQueries: async () => {
            await queryClient.invalidateQueries({
                queryKey: queryKeyPrefix,
                type: 'all',
            })
        },
        result: useQuery({
            queryKey,
            ...options,
            queryFn: async () => {
                const response = await makeCerebroApiRequest({
                    request: apiCall(params, apiOptions),
                })
                if (!response) {
                    return response
                }
                return formatApiResponse
                    ? formatApiResponse(response)
                    : response
            },
        }),
        updateRecord: (data: { rowIndex: number; record: any }) => {
            queryClient.setQueryData<CerebroPaginatedResponse<any> | null>(
                queryKey,
                (prev) => {
                    if (!prev) {
                        return prev
                    }

                    const newRecords = [...prev.data.results]
                    newRecords[data.rowIndex] = data.record

                    return {
                        ...prev,
                        data: {
                            ...prev.data,
                            results: newRecords,
                        },
                    }
                }
            )
        },
    }
}

export type CerebroPaginatedMetricDataQueryProps<T> =
    CerebroPaginatedDataQueryProps<T>

export type CerebroPaginatedMetricDataQueryResult<T> =
    CerebroPaginatedDataQueryResult<T>

export function useCerebroPaginatedMetricDataQuery<T>({
    queryKeyPrefix,
    params,
    options,
    apiCall,
    formatApiResponse,
}: CerebroPaginatedMetricDataQueryProps<T>): CerebroPaginatedMetricDataQueryResult<T> {
    return useCerebroPaginatedDataQuery({
        queryKeyPrefix,
        params,
        options,
        apiCall,
        formatApiResponse,
    })
}

interface CerebroPaginatedNonMetricDataQueryParamsProps {
    tablePath: CobaltPath
    filters: PageFilters
    currencyCode?: CurrencyCode
    groupBy?: string
    additionalParams?: Record<string, unknown>
    formatFilters: FormatBannerFiltersFn
}

export function useCerebroPaginatedNonMetricDataQueryParams({
    tablePath,
    filters,
    currencyCode,
    groupBy,
    additionalParams,
    formatFilters,
}: CerebroPaginatedNonMetricDataQueryParamsProps): Record<string, unknown> {
    const { org } = useOrgContext()
    const fiscalYearStartMonth = org?.fiscal_year_start_month
    const { pagination, sorter } = useSelector((state: RootReduxState) =>
        selectTableSettings(state, tablePath)
    )

    const additionalMetricParams = {
        ...additionalParams,
    }

    const params: Record<string, unknown> = {
        ...formatPagination(pagination),
        ...formatSorter(sorter),
        ...formatFilters(filters, false, fiscalYearStartMonth),
        ...formatCurrency(currencyCode),
        ...additionalMetricParams,
    }
    if (groupBy) {
        params.group_by = groupBy
    }

    return params
}

type CerebroPaginatedNonMetricDataQueryProps<T> = Omit<
    CerebroPaginatedMetricDataQueryProps<T>,
    'config' | 'metrics'
> & {
    params: Record<string, unknown>
}

export type CerebroPaginatedNonMetricDataQueryResult<T> =
    CerebroPaginatedMetricDataQueryResult<T>

export function useCerebroPaginatedNonMetricDataQuery<T>({
    queryKeyPrefix,
    options,
    params,
    apiCall,
}: CerebroPaginatedNonMetricDataQueryProps<T>): CerebroPaginatedNonMetricDataQueryResult<T> {
    const queryClient = useQueryClient()

    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })
    const { userOverride } = useUserOverrideContext()
    const { orgId } = useOrgContext()

    const apiOptions = {
        headers: {
            userId: userOverride?.userId,
            orgId: userOverride?.organizationId,
            orgGroupId: userOverride?.organizationGroupId,
        },
    }

    const queryKey = [
        queryKeyPrefix,
        'summary',
        orgId,
        {
            ...params,
        },
    ]

    return {
        invalidateAllRelatedQueries: async () => {
            await queryClient.invalidateQueries({
                queryKey: queryKeyPrefix,
                type: 'all',
            })
        },
        result: useQuery({
            queryKey,
            ...options,
            queryFn: () =>
                makeCerebroApiRequest({
                    request: apiCall(params, apiOptions),
                }),
        }),
        updateRecord: (data: { rowIndex: number; record: any }) => {
            queryClient.setQueryData<CerebroPaginatedResponse<any> | null>(
                queryKey,
                (prev) => {
                    if (!prev) {
                        return prev
                    }

                    const newRecords = [...prev.data.results]
                    newRecords[data.rowIndex] = data.record

                    return {
                        ...prev,
                        data: {
                            ...prev.data,
                            results: newRecords,
                        },
                    }
                }
            )
        },
    }
}

type CerebroDownloadQueryProps<T> = Omit<
    CerebroPaginatedMetricDataQueryProps<T>,
    'options' | 'apiCall'
> & {
    reportName: string
    apiCall: (
        params: Record<string, unknown>
    ) => Promise<CerebroResourceResponse<Report>>
    params: Record<string, unknown>
}

export interface CerebroDownloadQueryResult {
    triggerReport: () => void
    isPending: boolean
}

interface CerebroSimpleDownloadQueryProps {
    mutationKey: any
    params: Record<string, unknown>
    reportName: string
    apiCall: (
        params: Record<string, unknown>
    ) => Promise<CerebroResourceResponse<Report>>
}

export function useSimpleCerebroDownload({
    mutationKey,
    params,
    reportName,
    apiCall,
}: CerebroSimpleDownloadQueryProps): CerebroDownloadQueryResult {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const { mutate, isPending } = useMutation({
        mutationKey,
        mutationFn: async () => {
            const response = await makeCerebroApiRequest({
                request: apiCall(params),
            })
            notifyAsyncExportStarted({
                reportName,
                report: response?.data,
            })
            return response
        },
    })

    return useMemo(
        () => ({
            triggerReport: mutate,
            isPending,
        }),
        [mutate, isPending]
    )
}

interface CerebroDownloadParamsProps<T> {
    tablePath: CobaltPath
    columns: Field<T>[]
    filters: any
    currencyCode?: CurrencyCode
    additionalParams?: Record<string, unknown>
    groupBy?: string
    formatFilters: FormatBannerFiltersFn
    formatColumns?: (
        columns: Field<T>[],
        showPeriodDeltas: boolean
    ) => ColumnsFormatted
    reportName: string
}

export function useCerebroDownloadParams<T>({
    tablePath,
    columns,
    filters,
    currencyCode,
    additionalParams = {},
    groupBy,
    formatFilters,
    formatColumns,
    reportName,
}: CerebroDownloadParamsProps<T>): Record<string, unknown> {
    const { org } = useOrgContext()
    const fiscalYearStartMonth = org?.fiscal_year_start_month
    const currency = currencyCode

    const { sorter, showPeriodDeltas, periodDeltaType, periodDeltaDateRange } =
        useSelector((state: RootReduxState) =>
            selectTableSettings(state, tablePath)
        )
    const params: Record<string, unknown> = {
        ...formatFilters(filters, false, fiscalYearStartMonth),
        ...formatSorter(sorter),
        ...(formatColumns
            ? formatColumns(columns, showPeriodDeltas)
            : defaultFormatColumns(columns, showPeriodDeltas)),
        ...formatCurrency(currency),
        ...formatPeriodDeltaDateRange(
            showPeriodDeltas,
            periodDeltaType,
            periodDeltaDateRange,
            filters[DATES],
            'report_date',
            0,
            fiscalYearStartMonth
        ),
        ...additionalParams,
        group_by: groupBy,
        async_download_name: reportName,
    }
    return params
}

export function useCerebroDownload<T>({
    queryKeyPrefix,
    apiCall,
    params,
    reportName,
}: CerebroDownloadQueryProps<T>): CerebroDownloadQueryResult {
    const { orgId } = useOrgContext()

    const mutationKey = [
        queryKeyPrefix,
        'download',
        orgId,
        {
            ...params,
        },
    ]

    return useSimpleCerebroDownload({
        mutationKey,
        params,
        reportName,
        apiCall,
    })
}
